import {WorkflowNodeStyles, WorkflowNodeTypes} from "@/types/node";
import {capitalize, getTailwindColor} from "@/utils/index";
import {
  MessageSquareDashed,
  OctagonX,
  PhoneOff,
  TextCursorInput,
  Zap,
  AlignLeft,
  Workflow,
  Settings,
  Phone,
  Redo2,
  MessageSquare,
} from "lucide-react";
import {
  startNodeBlueprint,
  formNodeBlueprint,
  actionNodeBlueprint,
  talkNodeBlueprint,
  endNodeBlueprint,
  objectionNodeBlueprint,
  stepNodeBlueprint,
  jumpNodeBlueprint,
  branchNodeBlueprint,
  globalNodeBlueprint,
  instructionNodeBlueprint,
} from "./Blueprints";
import {getTailwindColors} from "@/constants/tailwindConfigs";

const TailwindColors = getTailwindColors();

const nodeStyles = {
  [WorkflowNodeTypes.ROOT]: {},
  [WorkflowNodeTypes.GLOBAL]: {
    iconColor: TailwindColors.secondary.DEFAULT,
    iconBackgroundColor: TailwindColors.muted.DEFAULT,
    iconBorderColor: "var(--border)",
    minimapNodeColor: TailwindColors.gray["100"],
    miniMapNodeSelectedColor: TailwindColors.gray["200"],
    contextColor: "bg-gray-100",
  },
  [WorkflowNodeTypes.START]: {
    iconColor: TailwindColors.green["600"],
    iconBackgroundColor: TailwindColors.green["50"],
    iconBorderColor: TailwindColors.green["100"],
    minimapNodeColor: TailwindColors.green["100"],
    miniMapNodeSelectedColor: TailwindColors.green["200"],
    contextColor: "bg-green-50",
  },
  [WorkflowNodeTypes.STEP]: {
    iconColor: TailwindColors.violet["500"],
    iconBackgroundColor: TailwindColors.violet["50"],
    iconBorderColor: TailwindColors.violet["100"],
    minimapNodeColor: TailwindColors.violet["100"],
    miniMapNodeSelectedColor: TailwindColors.violet["200"],
    contextColor: "bg-violet-50",
  },
  [WorkflowNodeTypes.BRANCH]: {
    iconColor: TailwindColors.violet["700"],
    iconBackgroundColor: TailwindColors.violet["50"],
    iconBorderColor: TailwindColors.violet["100"],
    minimapNodeColor: TailwindColors.violet["100"],
    miniMapNodeSelectedColor: TailwindColors.violet["200"],
    contextColor: "bg-violet-200",
  },
  [WorkflowNodeTypes.JUMP]: {
    iconColor: TailwindColors.blue["700"],
    iconBackgroundColor: TailwindColors.blue["50"],
    iconBorderColor: TailwindColors.blue["100"],
    minimapNodeColor: TailwindColors.blue["100"],
    miniMapNodeSelectedColor: TailwindColors.blue["200"],
    contextColor: "bg-blue-50",
  },
  [WorkflowNodeTypes.END]: {
    iconColor: TailwindColors.red["500"],
    iconBackgroundColor: TailwindColors.red["50"],
    iconBorderColor: TailwindColors.red["100"],
    minimapNodeColor: TailwindColors.red["100"],
    miniMapNodeSelectedColor: TailwindColors.red["200"],
    contextColor: "bg-red-50",
  },
  [WorkflowNodeTypes.PLACEHOLDER]: {
    iconClassName: "",
    iconWrapperClassName: "",
    minimapNodeColor: TailwindColors.transparent,
    miniMapNodeSelectedColor: TailwindColors.transparent,
  },
  [WorkflowNodeTypes.INSTRUCTION]: {
    iconColor: TailwindColors.violet["500"],
    iconBackgroundColor: TailwindColors.violet["50"],
    iconBorderColor: TailwindColors.violet["100"],
    minimapNodeColor: TailwindColors.violet["100"],
    miniMapNodeSelectedColor: TailwindColors.violet["200"],
    contextColor: "bg-violet-50",
  },

  // unused nodes
  [WorkflowNodeTypes.FORM]: {
    iconColor: TailwindColors.primary.accent,
    iconBackgroundColor: TailwindColors.violet["100"],
  },
  [WorkflowNodeTypes.ACTION]: {
    iconColor: TailwindColors.blue["700"],
    iconBackgroundColor: TailwindColors.blue["50"],
    iconBorderColor: TailwindColors.blue["100"],
  },
  [WorkflowNodeTypes.TALK]: {
    iconColor: TailwindColors.secondary.DEFAULT,
    iconBackgroundColor: TailwindColors.accent.DEFAULT,
  },
};

export const WorkflowNodesData: Record<WorkflowNodeTypes, WorkflowNodeStyles> =
  {
    [WorkflowNodeTypes.ROOT]: {
      name: capitalize(WorkflowNodeTypes.ROOT),
      icon: null,
      blueprint: null,
      styles: nodeStyles[WorkflowNodeTypes.ROOT],
    },
    [WorkflowNodeTypes.GLOBAL]: {
      name: capitalize(WorkflowNodeTypes.GLOBAL),
      icon: Settings,
      blueprint: globalNodeBlueprint,
      styles: nodeStyles[WorkflowNodeTypes.GLOBAL],
    },
    [WorkflowNodeTypes.START]: {
      name: capitalize(WorkflowNodeTypes.START),
      icon: Phone,
      blueprint: startNodeBlueprint,
      styles: nodeStyles[WorkflowNodeTypes.START],
    },
    [WorkflowNodeTypes.STEP]: {
      name: capitalize(WorkflowNodeTypes.STEP),
      icon: AlignLeft,
      blueprint: stepNodeBlueprint,
      styles: nodeStyles[WorkflowNodeTypes.STEP],
    },
    [WorkflowNodeTypes.BRANCH]: {
      name: capitalize(WorkflowNodeTypes.BRANCH),
      icon: Workflow,
      blueprint: branchNodeBlueprint,
      styles: nodeStyles[WorkflowNodeTypes.BRANCH],
    },
    [WorkflowNodeTypes.JUMP]: {
      name: capitalize(WorkflowNodeTypes.JUMP),
      icon: Redo2,
      blueprint: jumpNodeBlueprint,
      styles: nodeStyles[WorkflowNodeTypes.JUMP],
    },
    [WorkflowNodeTypes.END]: {
      name: capitalize(WorkflowNodeTypes.END),
      icon: PhoneOff,
      blueprint: endNodeBlueprint,
      styles: nodeStyles[WorkflowNodeTypes.END],
    },
    [WorkflowNodeTypes.PLACEHOLDER]: {
      name: capitalize(WorkflowNodeTypes.PLACEHOLDER),
      icon: null,
      blueprint: null,
      styles: nodeStyles[WorkflowNodeTypes.PLACEHOLDER],
    },
    [WorkflowNodeTypes.INSTRUCTION]: {
      name: capitalize(WorkflowNodeTypes.INSTRUCTION),
      icon: MessageSquare,
      blueprint: instructionNodeBlueprint,
      styles: nodeStyles[WorkflowNodeTypes.INSTRUCTION],
    },
    [WorkflowNodeTypes.FORM]: {
      name: capitalize(WorkflowNodeTypes.FORM),
      icon: TextCursorInput,
      blueprint: formNodeBlueprint,
      styles: nodeStyles[WorkflowNodeTypes.FORM],
    },
    [WorkflowNodeTypes.ACTION]: {
      name: capitalize(WorkflowNodeTypes.ACTION),
      icon: Zap,
      blueprint: actionNodeBlueprint,
      styles: nodeStyles[WorkflowNodeTypes.ACTION],
    },
    [WorkflowNodeTypes.TALK]: {
      name: capitalize(WorkflowNodeTypes.TALK),
      icon: MessageSquareDashed,
      blueprint: talkNodeBlueprint,
      styles: nodeStyles[WorkflowNodeTypes.TALK],
    },
    [WorkflowNodeTypes.OBJECTION]: {
      name: capitalize(WorkflowNodeTypes.OBJECTION),
      icon: OctagonX,
      blueprint: objectionNodeBlueprint,
      styles: nodeStyles[WorkflowNodeTypes.OBJECTION],
    },
  };
